import {AppleBusinessInput} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/apple/applebusinessinput';
import {AppleDelegationSelect} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/apple/appledelegationselect';
import {AppleConfiguration} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/appleconfiguration';
import {AppleCountryInfo} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/data/applecountryinfo';
import {LinkedAccounts} from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/linkedaccounts/linkedaccounts';
import {SkipSetup} from '/src/com/yext/listingslaunchstorm/js/enterprise/skipsetup/skipsetup';
import {CalloutBanner, CalloutBannerType} from '/ui/components/calloutbanner/calloutbanner';

import * as styles from '/src/com/yext/listingslaunchstorm/js/enterprise/setuppublisher/apple/apple.module.scss';

/**
 * @param {{
 *   appleConfiguration: AppleConfiguration,
 *   setAppleConfiguration: function(AppleConfiguration): void,
 * }} props
 */
export function AppleLinkedAccount({appleConfiguration, setAppleConfiguration}) {
  const [linkedAccount, setLinkedAccount] = React.useState(appleConfiguration.linkedAccount);
  const skipSetup = appleConfiguration.skipSetup || appleConfiguration.delegateAppleAccount;
  React.useEffect(() => {
    if (linkedAccount?.id !== appleConfiguration.linkedAccount?.id) {
      setAppleConfiguration(new AppleConfiguration({
        appleCountryInfos: [],
        linkedAccount: linkedAccount,
        publisherId: appleConfiguration.publisherId,
        delegateAppleAccount: appleConfiguration.delegateAppleAccount,
        skipSetup: appleConfiguration.skipSetup,
      }));
    }
  }, [linkedAccount, setAppleConfiguration, appleConfiguration]);

  /**
   * @param {Array<AppleCountryInfo>=} appleCountryInfos
   */
  const setCountryInfos = appleCountryInfos => {
    const linkedAccount = appleConfiguration.linkedAccount;
    const publisherId = appleConfiguration.publisherId;
    const skipSetup = appleConfiguration.skipSetup;
    const delegateAppleAccount = appleConfiguration.delegateAppleAccount;

    setAppleConfiguration(
      new AppleConfiguration({
        linkedAccount,
        appleCountryInfos,
        delegateAppleAccount,
        skipSetup,
        publisherId,
      }));
  };

  /**
   * @param {boolean=} shouldDelegate
   * @param {boolean=} shouldSkip
   * @returns {void}
   */
  const updateAppleConfiguration = (shouldDelegate, shouldSkip) => {
    setAppleConfiguration(
      new AppleConfiguration({
        appleCountryInfos: appleConfiguration.appleCountryInfos?.map(
          appleCountryInfo => ({
            ...appleCountryInfo,
            excluded: (shouldDelegate || shouldSkip) ?? false,
          }),
        ),
        delegateAppleAccount: shouldDelegate ? !appleConfiguration.delegateAppleAccount : false,
        skipSetup: shouldSkip ? !appleConfiguration.skipSetup : false,
        linkedAccount: appleConfiguration.linkedAccount,
        publisherId: appleConfiguration.publisherId,
      }),
    );
  };

  return (
    <>
      <LinkedAccounts
        publisherId={appleConfiguration.publisherId}
        linkedAccount={appleConfiguration.linkedAccount}
        setLinkedAccount={setLinkedAccount}
        isTest={false}
        skipSetup={skipSetup}
      />
      {appleConfiguration.linkedAccount && (
        <div className={styles.appleBusinessConfigSection}>
          <div>
            <CalloutBanner type={CalloutBannerType.NEUTRAL}>
              {yext.msg('Include the business ID for each country. By default, all countries are selected. If you wish to exclude a specific country, any listings from that country will be unavailable in Yext')}
            </CalloutBanner>
          </div>
          <div className={styles.appleBusinessConfigTable}>
            <AppleBusinessInput
              publisherId={appleConfiguration.publisherId}
              linkedAccount={appleConfiguration.linkedAccount}
              appleCountryInfos={appleConfiguration.appleCountryInfos}
              setCountryInfos={setCountryInfos}
              isDisabled={skipSetup}
            />
          </div>
        </div>
      )}
      <AppleDelegationSelect
        delegateAppleOwnership={
          appleConfiguration.delegateAppleAccount
          && !appleConfiguration.skipSetup
        }
        disabled={appleConfiguration.skipSetup}
        onClick={() => updateAppleConfiguration(true, false)}
        tooltipText={yext.msg('Using the Yext Apple account means you can’t make Apple social posts, use Apple analytics, and sync various fields.')}
      />
      <SkipSetup
        skipSetup={
          !appleConfiguration.delegateAppleAccount
          && appleConfiguration.skipSetup
        }
        disabled={appleConfiguration.delegateAppleAccount}
        onClick={() => updateAppleConfiguration(false, true)}
        tooltipText={yext.msg(
          'By skipping this step, you will not set up your configuration to send your entities to Apple. If you would like to set this up in the future, you can go through the Listings Launch flow again.',
        )}
      />
    </>
  );
}
